import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ChevronLeft, CheckCircle2, XCircle, MessagesSquare, ArrowUpRightSquare,
  Sparkles, History, ShieldAlert, FileText,
} from "lucide-react";
import { getArtifact, decideArtifact, aiDraft } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { fmtDate, fmtTime, fmtRelative } from "@/lib/format";
import StatusBadge from "@/components/StatusBadge";
import ConfidenceMeter from "@/components/ConfidenceMeter";
import ContributorClassTag from "@/components/ContributorClassTag";
import PolicyFlag from "@/components/PolicyFlag";
import WordDiff from "@/components/WordDiff";
import { toast } from "sonner";

export default function ArtifactReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [art, setArt] = useState(null);
  const [draft, setDraft] = useState("");
  const [showDiff, setShowDiff] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [comment, setComment] = useState("");
  const [policyGate, setPolicyGate] = useState(null); // { decision, flags }
  const [overrideReason, setOverrideReason] = useState("");

  useEffect(() => {
    getArtifact(id)
      .then((a) => { setArt(a); setDraft(a.draft || ""); })
      .catch((err) => toast.error(err?.response?.data?.detail || "Failed to load artifact"));
  }, [id]);

  const decide = useCallback(async (decision, { policy_override = false, policy_override_reason = undefined } = {}) => {
    if (!art) return;
    try {
      const r = await decideArtifact(id, {
        decision,
        comment,
        edits: draft !== art.draft ? draft : undefined,
        policy_override,
        policy_override_reason,
      });
      setArt(r.artifact);
      setComment("");
      toast.success(`Decision recorded: ${decision.replace(/_/g, " ")}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "POLICY_GATE") {
        setPolicyGate({ decision, flags: detail.flags });
        setOverrideReason("");
      } else {
        toast.error(err?.response?.data?.detail || "Could not record decision");
      }
    }
  }, [id, art, draft, comment]);

  const refineWithAI = useCallback(async () => {
    setAiLoading(true);
    try {
      const r = await aiDraft(id, "Tighten the proposed artifact, keep it factual, enterprise-grade, no hype.");
      setDraft((prev) => r.refined_draft || prev);
      toast.success("AI-assisted refinement applied. Review before approving.");
    } catch {
      toast.error("AI drafting is temporarily unavailable");
    } finally {
      setAiLoading(false);
    }
  }, [id]);

  // Bridge: react to Cmd-K actions dispatched by CommandPalette
  useEffect(() => {
    const onCmd = (e) => {
      const kind = e.detail?.kind;
      if (!kind) return;
      if (kind === "ai-refine") refineWithAI();
      else if (kind === "approve") decide("approve");
      else if (kind === "reject") decide("reject");
      else if (kind === "toggle-diff") setShowDiff((s) => !s);
    };
    window.addEventListener("proofmark:cmd", onCmd);
    return () => window.removeEventListener("proofmark:cmd", onCmd);
  }, [decide, refineWithAI]);

  // Local keyboard shortcuts when on review page
  useEffect(() => {
    const onKey = (e) => {
      if (!art) return;
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      const k = e.key.toLowerCase();
      if (k === "i" && !e.shiftKey) { e.preventDefault(); refineWithAI(); }
      else if (k === "d" && !e.shiftKey) { e.preventDefault(); setShowDiff((s) => !s); }
      else if (k === "a" && e.shiftKey) { e.preventDefault(); decide("approve"); }
      else if (k === "r" && e.shiftKey) { e.preventDefault(); decide("reject"); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [art, decide, refineWithAI]);

  if (!art) return <div className="p-10 text-muted-foreground">Loading…</div>;

  return (
    <div className="flex flex-col h-[calc(100vh-65px)]" data-testid="artifact-review-page">
      <Dialog open={!!policyGate} onOpenChange={(open) => { if (!open) setPolicyGate(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Policy flags require acknowledgement</DialogTitle>
            <DialogDescription>
              This artifact has active policy flags. To approve, provide a documented override reason
              that will be written to the immutable evidence record.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-1">
              {(policyGate?.flags || []).map((f) => (
                <div key={f} className="flex items-center gap-2 text-sm text-amber-900">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-600 shrink-0" />
                  {f}
                </div>
              ))}
            </div>
            <div>
              <label className="text-sm font-medium block mb-1.5">Override reason (required)</label>
              <Textarea
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                placeholder="Explain why this artifact should be approved despite the flags…"
                className="min-h-[80px]"
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={() => setPolicyGate(null)}
                className="rounded-md border border-border bg-card px-4 py-2 text-sm hover:bg-muted"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!overrideReason.trim()) {
                    toast.error("Override reason is required");
                    return;
                  }
                  const d = policyGate.decision;
                  setPolicyGate(null);
                  await decide(d, { policy_override: true, policy_override_reason: overrideReason });
                }}
                className="rounded-md bg-brand text-brand-foreground px-4 py-2 text-sm font-medium hover:opacity-90"
              >
                Approve anyway
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <div className="flex items-center justify-between px-8 py-4 border-b border-border/80">
        <div className="flex items-center gap-4 min-w-0">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="review-back">
            <ChevronLeft className="h-4 w-4" /> Back
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <span className="font-mono text-xs text-muted-foreground">{art.id}</span>
              <StatusBadge value={art.status} />
            </div>
            <h2 className="mt-1 font-serif-display text-xl leading-tight truncate">{art.title}</h2>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => decide("send_back")} className="rounded-md border border-border bg-card px-3 py-2 text-sm hover:bg-muted" data-testid="review-send-back">
            Send back
          </button>
          <button onClick={() => decide("dispute")} className="rounded-md border border-rose-200 bg-rose-50 text-rose-800 px-3 py-2 text-sm hover:bg-rose-100" data-testid="review-dispute">
            Dispute
          </button>
          <button onClick={() => decide("approve")} className="rounded-md bg-brand text-brand-foreground px-3 py-2 text-sm font-medium hover:opacity-90" data-testid="review-approve">
            Approve
          </button>
        </div>
      </div>

      <div className="grid grid-cols-12 flex-1 min-h-0">
        {/* LEFT: SUMMARY */}
        <aside className="col-span-3 border-r border-border/80 bg-muted/20 overflow-y-auto">
          <div className="p-6 space-y-6">
            <section>
              <div className="tracked-caps">Summary</div>
              <dl className="mt-3 space-y-2.5 text-sm">
                <Row k="Type">{art.type}</Row>
                <Row k="Domain">{art.domain}</Row>
                <Row k="Frequency"><span className="font-mono">{art.frequency} cases</span></Row>
                <Row k="Confidence"><ConfidenceMeter value={art.confidence} /></Row>
                <Row k="Generated"><span className="font-mono text-xs text-muted-foreground">{fmtDate(art.created_at)}</span></Row>
                <Row k="Reviewer">{art.assigned_reviewer}</Row>
                <Row k="Target">{art.publication_target}</Row>
              </dl>
            </section>

            <section>
              <div className="tracked-caps">Contributor classes</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {art.contributor_classes.map((c) => (
                  <ContributorClassTag key={c} value={c} count={art.contributor_count} />
                ))}
              </div>
            </section>

            <section>
              <div className="tracked-caps">Source set</div>
              <ul className="mt-3 space-y-1.5 font-mono text-xs">
                {art.source_refs.map((s) => (
                  <li key={s} className="flex items-center justify-between rounded border border-border bg-card px-2.5 py-1.5">
                    <span>{s}</span>
                    <ArrowUpRightSquare className="h-3 w-3 text-muted-foreground" />
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <div className="tracked-caps">Review checklist</div>
              <ul className="mt-3 space-y-2 text-sm">
                {["Source set validated", "Contributor class confirmed", "Policy flags cleared", "Publication target correct"].map((c, i) => (
                  <li key={c} className="flex items-start gap-2">
                    <CheckCircle2 className={`h-4 w-4 mt-0.5 ${i < 2 ? "text-emerald-600" : "text-muted-foreground"}`} />
                    <span className={i < 2 ? "" : "text-muted-foreground"}>{c}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        </aside>

        {/* CENTER: DRAFT */}
        <section className="col-span-6 overflow-y-auto">
          <div className="p-8 space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="tracked-caps">Proposed artifact</div>
                <h3 className="mt-1 font-serif-display text-2xl">Editable draft</h3>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowDiff((s) => !s)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs hover:bg-muted"
                  data-testid="review-toggle-diff"
                >
                  <History className="h-3.5 w-3.5" /> {showDiff ? "Hide" : "Show"} existing vs proposed
                </button>
                <button
                  onClick={refineWithAI}
                  disabled={aiLoading}
                  className="inline-flex items-center gap-1.5 rounded-md border border-brand bg-brand/5 text-brand px-3 py-1.5 text-xs hover:bg-brand/10 disabled:opacity-60"
                  data-testid="review-ai-refine"
                >
                  <Sparkles className="h-3.5 w-3.5" /> {aiLoading ? "Refining…" : "AI-assisted refine"}
                </button>
              </div>
            </div>

            {showDiff && (
              <WordDiff existing={art.existing_content || ""} proposed={draft} />
            )}

            <div className="rounded-md border border-border bg-card">
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-border/80">
                <div className="flex items-center gap-2">
                  <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="tracked-caps">Draft body</span>
                </div>
                <span className="font-mono text-[11px] text-muted-foreground">{draft.length} chars</span>
              </div>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                data-testid="review-draft-textarea"
                spellCheck={false}
                className="w-full min-h-[280px] px-4 py-4 font-mono text-[13px] leading-relaxed bg-transparent outline-none resize-y"
              />
            </div>

            <div className="rounded-md border border-border bg-card p-4">
              <div className="tracked-caps flex items-center gap-2"><MessagesSquare className="h-3.5 w-3.5" /> Reviewer comment</div>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Record the reasoning for your decision. It will be written to the evidence record."
                data-testid="review-comment"
                className="mt-3 w-full min-h-[80px] rounded border border-border bg-background px-3 py-2 text-sm outline-none focus:border-brand"
              />
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                <button onClick={() => decide("approve")} className="rounded-md bg-brand text-brand-foreground py-2 text-sm font-medium hover:opacity-90" data-testid="review-approve-main">
                  Approve
                </button>
                <button onClick={() => decide("approve_with_edits")} className="rounded-md border border-brand text-brand py-2 text-sm hover:bg-brand/5" data-testid="review-approve-edits">
                  Approve with edits
                </button>
                <button onClick={() => decide("escalate")} className="rounded-md border border-border bg-card py-2 text-sm hover:bg-muted" data-testid="review-escalate">
                  Escalate
                </button>
                <button onClick={() => decide("reject")} className="inline-flex items-center justify-center gap-1.5 rounded-md border border-rose-200 bg-rose-50 text-rose-800 py-2 text-sm hover:bg-rose-100" data-testid="review-reject">
                  <XCircle className="h-4 w-4" /> Reject
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* RIGHT: EVIDENCE / POLICY / TIMELINE */}
        <aside className="col-span-3 border-l border-border/80 bg-muted/20 overflow-y-auto">
          <div className="p-6 space-y-6">
            <section>
              <div className="tracked-caps flex items-center gap-2"><ShieldAlert className="h-3.5 w-3.5" /> Policy flags</div>
              <div className="mt-3 flex flex-col gap-2">
                {art.policy_flags.length === 0 ? (
                  <span className="text-xs text-muted-foreground">No flags triggered.</span>
                ) : (
                  art.policy_flags.map((f) => <PolicyFlag key={f} label={f} severity="medium" />)
                )}
              </div>
            </section>

            <section>
              <div className="tracked-caps">Evidence record</div>
              <div className="mt-3 rounded-md border border-border bg-card p-4 text-sm space-y-2">
                <Row k="Completeness">
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-20 rounded-full bg-muted overflow-hidden">
                      <div className="h-full bg-emerald-600" style={{ width: "65%" }} />
                    </div>
                    <span className="font-mono text-xs">65%</span>
                  </div>
                </Row>
                <Row k="Policy basis">Operational pattern; PII redaction applied</Row>
                <Row k="Retention">7 years</Row>
                <Link to={`/app/evidence`} className="mt-2 inline-flex items-center gap-1 text-xs text-brand hover:underline">
                  Open in Evidence Console <ArrowUpRightSquare className="h-3 w-3" />
                </Link>
              </div>
            </section>

            <section>
              <div className="tracked-caps">Decision history</div>
              <ol className="mt-3 relative border-l border-border/80 ml-2 pl-5 space-y-4">
                {art.review_timeline.map((t) => (
                  <li key={`${t.ts}-${t.actor}-${t.action}`} className="relative">
                    <span className="absolute -left-[23px] top-1 h-2 w-2 rounded-full bg-brand" />
                    <div className="text-sm">{t.actor}</div>
                    <div className="text-xs text-muted-foreground">{t.action}</div>
                    <div className="font-mono text-[11px] text-muted-foreground mt-0.5">{fmtDate(t.ts)} · {fmtTime(t.ts)} · {fmtRelative(t.ts)}</div>
                  </li>
                ))}
              </ol>
            </section>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Row({ k, children }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="tracked-caps text-[10px]">{k}</dt>
      <dd className="text-right text-sm">{children}</dd>
    </div>
  );
}
